import { darken } from 'polished';
import Quill from 'quill';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { styled } from 'styled-components';
import { classNames, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { Fauxdown } from '../../../concepts/text/Fauxdown';
import { FormattingOptionItem } from '../../../concepts/text/FormattingOptionItem';
import { Colors, Float, toRem } from '../../../ui';
import { textAlignmentToFlexAlign, textAlignmentToTextAlign, TextMargin, TextSettings } from '../../canvas/entities/TextConventions';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { AutoFormat } from './ext/AutoFormatModule';
import { CustomToolbar } from './ext/CustomToolbarModule';
import { MentionBlot } from './ext/MentionBlot';
import { AtsertWidget, Mention } from './ext/MentionModule';
import './mention.css';

Quill.register({ 'modules/autoFormat': AutoFormat });
Quill.register({ 'modules/customToolbar': CustomToolbar });
Quill.register({ 'modules/mention': Mention, 'blots/mention': MentionBlot });

const List = Quill.import('formats/list') as any;
const originalCreate = List.create;
List.create = function (value) {
    if (value === 'bullet' || value === 'ordered') {
        return originalCreate.call(this, value);
    }
    return value;
};

Quill.register(List, true);

export type QuillEnterMode = 'commit' | 'newline';
export type QuillCommitSource = 'enter' | 'unmount';

export type QuillInputProps = {
    initialValue: string,
    instanceRef?: React.MutableRefObject<Quill>,
    onChange(next: string): void,
    onCommit?(final: string, source: QuillCommitSource): void,
    atsert?: AtsertWidget,
    autoFocus?: boolean,
    formatting: FormattingOptionItem[],
    enterMode?: QuillEnterMode,
    maxLength?: number,
    minHeight?: number,
    placeholder?: string,
    overflow?: string,
    textSettings?: Partial<TextSettings>,
};

const QuillMount = styled.div<TextSettings & { minHeight: number, overflow?: string }>`

    position: relative;
    height: 100%;

    .ql-editor {
        display: flex;
        flex-direction: column;
        justify-content: ${p => textAlignmentToFlexAlign(p.textAlignV) || 'start'};
        color: ${p => p.fontColor ? p.fontColor : '#000'};
        font-family: ${p => p.fontFamily ? p.fontFamily : 'Inter'};
        font-weight: ${p => p.fontWeight ? p.fontWeight : 400};
        font-size: ${p => p.fontSize ? p.fontSize : 24}px;
        text-align: ${p => textAlignmentToTextAlign(p.textAlignH) || 'left'};
        line-height: ${p => p.lineHeight};
        padding: ${p => TextMargin.toPx(p.textMargin)};
        min-height: ${p => p.minHeight ? toRem(p.minHeight) : 'auto'};

        overflow: ${p => p.overflow || 'hidden'};
        cursor: text;
    }

    .ql-editor.ql-blank::before {
        color: ${darken(.2, Colors.l1)};
        font-style: normal;
        margin-left: -6px;
    }
`;

const QuillToolbarFrame = styled.div`
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    transform: translate(-50%, -100%);
    padding: 1rem;
`;

const QuillToolbar = ({ id, formatting }: { id: string, formatting: FormattingOptionItem[] }) =>
    ReactDOM.createPortal(
        <QuillToolbarFrame id={`${id}-frame`} className="ignore-adapter ignore-comment">
            <Float inline>
                <Flexer id={id}>
                    {formatting.includes('header') &&
                        <span className="ql-formats">
                            <button className="ql-header" value="1"></button>
                            <button className="ql-header" value="2"></button>
                            <button className="ql-header" value="3"></button>
                        </span>
                    }
                    {formatting.includes('list') &&
                        <span className="ql-formats">
                            <button className="ql-list" value="ordered"></button>
                            <button className="ql-list" value="bullet"></button>
                        </span>
                    }
                    {formatting.includes('bold') &&
                        <span className="ql-formats">
                            <button className="ql-bold"></button>
                            <button className="ql-italic"></button>
                        </span>
                    }
                </Flexer>
            </Float>
        </QuillToolbarFrame>,
        document.body
    );

export const QuillInput = React.memo<QuillInputProps>((props) => {

    const {
        initialValue,
        instanceRef,
        onChange,
        onCommit,
        enterMode,
        formatting,
        atsert,
        textSettings: settings,
        placeholder,
        autoFocus,
        maxLength,
        minHeight,
        overflow
    } = props;

    const finalSettings = { ...TextSettings.default, ...settings };

    const mountProps = {
        overflow,
        minHeight: minHeight ?? 0,
        ...finalSettings,
        ...classNames(finalSettings.fontFamily)
    };

    const [toolbarId] = useState(() => `t${uuid('nodash').slice(0, 8)}`);

    const [quillElmt, setQuillElmt] = useState(null as HTMLDivElement);
    const quillInstance = useRef(null as Quill); // Actual instance of Quill

    const onLoad = (formattedString: string) => {
        if (!quillElmt) return;

        // Quill removes a trailing blank line so have to add one back in
        const html = Fauxdown.toEditorForm(formattedString, formatting) + '<p><br></p>';
        const delta = quillInstance.current.clipboard.convert({ html });

        quillInstance.current.setContents(delta);
        // Loading is included in history, so clear to prevent undo wiping the contnet
        quillInstance.current.history.clear();
    };

    const onUpdate = (_delta) => {
        if (maxLength && quillInstance.current.getLength() > maxLength) {
            quillInstance.current.deleteText(maxLength, quillInstance.current.getLength());
        }

        const html = quillInstance.current.root.innerHTML;
        const formattedString = Fauxdown.toSavedForm(html, formatting);

        onChange(formattedString);
    };

    const onCallCommit = useCallback((source: QuillCommitSource) => {
        const html = quillInstance.current.root.innerHTML;
        const formattedString = Fauxdown.toSavedForm(html);
        onCommit?.(formattedString, source);
    }, [onCommit]);

    useEffect(() => () => onCallCommit('unmount'), []);

    useEffect(() => {
        if (!quillElmt) return;

        const quill = new Quill(quillElmt, {
            formats: [
                ...formatting,
            ],
            modules: {
                // Custom
                autoFormat: {
                    bold: formatting.includes('bold'),
                    italic: formatting.includes('italic'),
                    header: formatting.includes('header'),
                },
                customToolbar: {
                    toolbarId,
                },
                mention: {
                    allowedChars: /^[\p{L}\p{N}_]*$/u,
                    mentionDenotationChars: ['@'],
                    widget: atsert
                },
                // Standard
                keyboard: {
                    bindings: {
                        tab: {
                            key: 9, // For some reason if we use "Tab" it fails
                            handler: () => false,
                        },
                        enterMode: {
                            key: 'Enter',
                            handler: () => {
                                if (enterMode === 'commit') {
                                    onCallCommit('enter');
                                }
                                return enterMode !== 'commit';
                            }
                        },
                        enterModeAlt: {
                            key: 'Enter',
                            ctrlKey: true,
                            handler: (range, context) => quill.keyboard.handleEnter(range, context)
                        }
                    }
                },
                toolbar: formatting.includes('header') || formatting.includes('list') ? `#${toolbarId}` : false,
            },
            placeholder,
            theme: 'snow',

        });

        quillInstance.current = quill;
        if (instanceRef) {
            instanceRef.current = quill;
        }

        quill.on('text-change', onUpdate);

        onLoad(initialValue || '');

        if (autoFocus) {
            quill.focus();
        }

        quill.setSelection(0, quill.getLength());

    }, [quillElmt]);


    return <>
        <QuillMount ref={setQuillElmt} {...mountProps} />
        <QuillToolbar id={toolbarId} formatting={formatting} />
    </>;
});
